import { Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { emptyCollectionObj } from '@app/account/profile/user-profile.variables';
import { DictionaryService } from '@app/core/services/bff/dictionary.service';
import { HelpersBffService } from '@app/core/services/bff/helpers.service';
import { UserProfileBffService } from '@app/core/services/bff/user-profile.service';
import { LoadingBarService } from '@core/components/spinners/loading-bar/loading-bar.service';
import {
  AuthService,
  CollectionsService,
  CommentService,
  ContentService,
  ContextService,
  FeaturesService,
  AnalyticsService,
  LessonsService,
  MetaService,
  ProfileService,
  UserServicesService,
  LanguageService,
  AccountService,
} from '@core/services';
import { LibraryRouter } from '@models/library.router';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import {
  Collection,
  CollectionDetailsType,
  CollectionItemDetailsType,
  Content,
  FindCollectionStrategyEnum,
  IContentPane,
  IFindCollectionFilter,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, finalize, map, takeUntil, tap } from 'rxjs/operators';
import { BaseUserProfileSubpageComponent } from '../base-user-profile-subpage.component';

@Component({
  selector: 'app-user-profile-collections-subpage',
  templateUrl: './user-profile-collections-subpage.component.html',
  styleUrls: ['./user-profile-collections-subpage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileCollectionsSubpageComponent extends BaseUserProfileSubpageComponent implements OnInit {
  @Output() public loaded = new EventEmitter<boolean>();

  private readonly collectionsSubject = new BehaviorSubject<IContentPane>({
    format: null,
    loading: false,
    loaded: false,
    collections: [],
    pagination: {
      page: 1,
      pageSize: 10,
      total: null,
      totalPages: null,
    },
    order: null,
  });

  public readonly collections$: Observable<IContentPane> = this.collectionsSubject.asObservable();
  public openedCardIndex: number | null = null;
  public item: Collection | null = null;
  public showModal = false;
  private originalUrl: string | null = null;
  private readonly collectionRoute = LibraryRouter.collectionRouteUniversal;
  public readonly emptyCollectionObj = emptyCollectionObj;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    protected router: Router,
    protected authService: AuthService,
    protected contextService: ContextService,
    protected profileService: ProfileService,
    protected accountService: AccountService,
    protected dictionaryService: DictionaryService,
    protected languageService: LanguageService,
    protected userProfileBff: UserProfileBffService,
    protected helpersBffService: HelpersBffService,
    protected features: FeaturesService,
    protected meta: MetaService,
    protected cdr: ChangeDetectorRef,
    public deviceService: DeviceDetectorService,
    protected loadingBar: LoadingBarService,
    private collectionsService: CollectionsService,
    protected location: Location,
    protected analytics: AnalyticsService,
  ) {
    super(
      router,
      authService,
      contextService,
      profileService,
      dictionaryService,
      languageService,
      userProfileBff,
      features,
      meta,
      loadingBar,
      cdr,
    );
  }

  ngOnInit(): void {
    this.loading = true;

    combineLatest([this.loadUserData$])
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.loading = false;
          this.detectChanges();
        }),
      )
      .subscribe({
        next: () => this.loadCollections(),
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.loaded.emit(true);
          this.detectChanges();
        },
      });
  }

  protected loadCollections(): void {
    if (!this._user || this.collectionsSubject.value.loading) {
      return;
    }

    this.isMe = this._me?.id === this._user.id;
    this.collectionsSubject.next({ ...this.collectionsSubject.value, loading: true });

    this.userProfileBff
      .getUserCollectionOfLessons({
        authorId: this._user.id,
        page: this.collectionsSubject.value.pagination.page,
        pageSize: this.collectionsSubject.value.pagination.pageSize,
      })
      .pipe(
        map((result) => ({
          ...this.collectionsSubject.value,
          collections: [
            ...this.collectionsSubject.value.collections,
            ...this.helpersBffService.convertBffCollectionsToLegacy(result.data),
          ],
          pagination: result.pagination,
          loading: false,
          loaded: true,
        })),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (updatedCollections) => {
          this.collectionsSubject.next(updatedCollections);
          this.loaded.emit(true);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.collectionsSubject.next({ ...this.collectionsSubject.value, loading: false });
          this.loaded.emit(true);
          this.detectChanges();
        },
      });
  }

  public get collections(): Collection[] {
    return this.collectionsSubject.value.collections || [];
  }

  public trackContentByFn(index: number, item: Content): string {
    return item.id;
  }

  public onOpenCard(collection: Collection): void {
    this.openedCardIndex = this.collections.findIndex((el) => el.id === collection.id);
    this.item = collection;

    this.accountService
      .getUserById(collection.author_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (account) => {
          this.item = { ...this.item, author: account };
          this.loadCollectionItems(this.item);
          this.updateUrl();
          this.showModal = true;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-USER');
        },
      });
  }

  private updateUrl(): void {
    if (!this.item) {
      return;
    }

    const url = this.collectionRoute(this.item);
    const currentUrl = this.location.path(false);

    if (currentUrl !== url) {
      if (!this.originalUrl) {
        this.originalUrl = currentUrl;
      }
      this.location.replaceState(url);
      this.analytics.pageView(url, currentUrl);
    }
  }

  protected loadCollectionItems(item: Collection): void {
    this.collectionsService
      .getItemsById(item.id, this.itemDetails)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (items) => {
          this.item = new Collection({ ...item, items: items.results });
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onAction(): void {
    this.loadCollections();
    this.markForCheck();
  }

  public onCloseCard(): void {
    this.openedCardIndex = null;
    this.item = null;
    this.showModal = false;

    if (this.originalUrl) {
      this.location.replaceState(this.originalUrl);
    }
    this.detectChanges();
  }

  protected get details(): CollectionDetailsType[] {
    return [
      'id',
      'slug',
      'author:sm',
      'author_id',
      'type',
      'title',
      'excerpt',
      'subject',
      'language',
      'multi_cover:lg:md',
      'price_tiers',
      'discount',
      'access',
      'stats',
      'visit_info',
      'options',
    ];
  }

  public readonly itemDetails: CollectionItemDetailsType[] = ['id', 'content_id', 'is_open', 'sort_order', 'content'];
}
